import { useTranslation } from "@i18n";
import {
  EstimateGeneralFieldKey,
  estimateGeneralSettingFields,
  isEstimateRequiredPermission,
  numberingSystemOptions,
  saveEstimateGeneralSettings,
} from "@Actions/estimation";
import {
  decimalPlacesDataSource,
  digitGroupingDataSource,
  largeNumberAbbreviationDataSource,
} from "@Helpers/estimateConsts";
import Accordian from "@CubeComponents/html/accordion/materialUiAccordian";
import { AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { folderPermissions } from "@Helpers/constants";
import { useDispatch, useSelector } from "react-redux";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import DisplayUnits from "./DisplayUnits";
import { getSymbolForNumber } from "@Services/utils/helpers";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import { getEstimateData } from "@Actions/EstimateList";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import type { RootState } from "src/reducers";
import type {
  EstimateData as EstimateDataState,
  SaveEstimateSettingsParams as saveEstimateSettingsParams,
} from "@ServerTypes/estimates/estimates.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
const { RESOURCE_MODIFY } = folderPermissions();

type PreferenceFieldKey = Exclude<keyof saveEstimateSettingsParams, "estimateId" | "numberingSystem">;
type PreferencesFieldType = "generalPreference" | "quantityRoundingPreference" | "rateAmountRoundingPreference";

const EstimateSettings = () => {
  const { t } = useTranslation();
  const [accordianState, setAccordianState] = useState<{ [k in EstimateGeneralFieldKey]?: boolean }>(
    Object.fromEntries(
      (Object.keys(estimateGeneralSettingFields) as EstimateGeneralFieldKey[]).map((field) => [field, true]),
    ),
  );
  const [buttonLoading, setButtonLoading] = useState(false);
  const [numberingSystemDataSource, setNumberingSystemDataSource] = useState<
    Array<{ id: number; level: number; numberingSystem: number; preview: number }>
  >([]);
  const [preferencesFields, setPreferencesFields] = useState<
    Array<{
      text: string;
      value: number | string;
      dataSource: Array<{ text: string; value: number | string; symbol?: string; example?: string }>;
      field: PreferenceFieldKey;
      type: PreferencesFieldType;
    }>
  >([]);

  const dataGrid = useRef<{ dataView: { items: Array<{ numberingSystem: number }> }; refresh: () => void } | null>(
    null,
  );
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString }>();
  const dispatch = useDispatch();
  const buttonLoadingRef = useRef(buttonLoading);

  useEffect(() => {
    buttonLoadingRef.current = buttonLoading;
  }, [buttonLoading]);

  const estimateVersionId = params.versionId;
  const actionPermissionAllowed = isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id);

  const generateNumberingSystemPreview = (level: number) => {
    const symbolArray =
      dataGrid.current?.dataView.items
        .slice(0, level)
        .map(({ numberingSystem }: { numberingSystem: number }) => getSymbolForNumber(1, numberingSystem)) ?? [];
    return symbolArray.join(".");
  };

  useEffect(() => {
    if (estimateData._id) {
      const settings = estimateData.settings!;
      const decimalPlaces = settings.decimalPlaces!;
      setNumberingSystemDataSource(
        settings.numberingSystem!.map((field: number, index: number) => {
          return {
            id: index + 1,
            level: index + 1,
            numberingSystem: field,
            preview: index + 1,
          };
        }),
      );

      setPreferencesFields([
        {
          text: t("Digit Grouping"),
          value: settings.digitGrouping ?? "",
          dataSource: digitGroupingDataSource,
          field: "digitGrouping",
          type: "generalPreference",
        },
        {
          text: t("Large Number Abbreviation"),
          value: settings.largeNumber ?? "",
          dataSource: largeNumberAbbreviationDataSource,
          field: "largeNumber",
          type: "generalPreference",
        },
        {
          text: t("Quantity"),
          value: decimalPlaces.quantity ?? 0,
          dataSource: decimalPlacesDataSource,
          field: "quantity",
          type: "quantityRoundingPreference",
        },
        {
          text: t("Measurement"),
          value: decimalPlaces.measurement ?? 0,
          dataSource: decimalPlacesDataSource,
          field: "measurement",
          type: "quantityRoundingPreference",
        },
        {
          text: t("Takeoff"),
          value: decimalPlaces.takeoff ?? 0,
          dataSource: decimalPlacesDataSource,
          field: "takeoff",
          type: "quantityRoundingPreference",
        },
        {
          text: t("Rate"),
          value: decimalPlaces.rate ?? 0,
          dataSource: decimalPlacesDataSource,
          field: "rate",
          type: "rateAmountRoundingPreference",
        },
        {
          text: t("Amount"),
          value: decimalPlaces.amount ?? 0,
          dataSource: decimalPlacesDataSource,
          field: "amount",
          type: "rateAmountRoundingPreference",
        },
      ]);
    }
  }, [estimateData]);

  const fieldItemTemplate = (
    option: { text: string; value: string | number; symbol?: string; example?: string },
    _isSelected: boolean,
  ) => (
    <div className="py-2 w-full text-sm overflow-auto">
      <div className="flex w-full justify-between gap-2">
        {option.text}
        {option.symbol && <div>{option.symbol}</div>}
        {option.example && <div>{option.example}</div>}
      </div>
    </div>
  );

  const numeringSystemItemTemplate = (option: { item: string; value: number }, _isSelected: boolean) => (
    <div className="py-1 w-full text-sm">{option.item}</div>
  );

  const preferencesFieldsChangeHandler = (field: PreferenceFieldKey) => async (args: { value: string | number }) => {
    setButtonLoading(true);
    const payload: saveEstimateSettingsParams = {
      estimateId: params.estimateId!,
      [field]: args.value,
    };
    const response = await saveEstimateGeneralSettings(payload);

    if (response.data.status) {
      setPreferencesFields((prev) => {
        let updatedData = [...prev];
        updatedData = updatedData.map((val) => {
          if (val.field == field) {
            val.value = args.value;
          }
          return val;
        });
        return updatedData;
      });
      dispatch(getEstimateData({ estimateId: params.estimateId! }));
      dispatch(showToastNotification({ content: t("Settings saved successfully!"), type: TOAST_TYPE_SUCCESS }));
    }
    setButtonLoading(false);
  };

  const numberingSystemOnChange = (level: number) => async (args: { value: number }) => {
    setButtonLoading(true);
    const response = await saveEstimateGeneralSettings({
      estimateId: params.estimateId!,
      numberingSystem: {
        level: level - 1,
        value: args.value,
      },
    });

    if (response.data.status) {
      setNumberingSystemDataSource((prev) => {
        const next = [...prev];
        const current = next[level - 1];
        if (current) {
          next[level - 1] = { ...current, numberingSystem: args.value };
        }
        return next;
      });
      dispatch(getEstimateData({ estimateId: params.estimateId! }));
      dispatch(showToastNotification({ content: t("Numbering System saved successfully!"), type: TOAST_TYPE_SUCCESS }));
    }
    dataGrid.current?.refresh();
    setButtonLoading(false);
  };

  const preferencesContent = (type: PreferencesFieldType = "generalPreference") => (
    <div
      className={`w-full grid grid-cols-1 ${type == "generalPreference" ? "xl:grid-cols-2" : "lg:grid-cols-2"} text-sm font-semibold gap-8`}
    >
      {preferencesFields
        .filter((field) => field.type == type)
        .map((preference) => {
          return (
            <div className="flex gap-2" key={preference.field}>
              <div className="flex items-center min-w-44">{preference.text}</div>
              <SelectDropDown
                id={preference.field}
                renderOption={fieldItemTemplate}
                onChange={(selected) => {
                  if (selected && !Array.isArray(selected)) preferencesFieldsChangeHandler(preference.field)(selected);
                }}
                value={preference.dataSource.find((item) => item.value === preference.value)!}
                fields={{ text: "text", value: "value" }}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                options={preference.dataSource}
                // buttonWidth="100%"
                disabled={!actionPermissionAllowed || buttonLoading || !!estimateVersionId}
              />
            </div>
          );
        })}
    </div>
  );

  const numberingSystemTemplate = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    args: { level: number; numberingSystem: number },
  ) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);
    root.render(
      <SelectDropDown
        id={`numberingSystem_${args.level}`}
        renderOption={numeringSystemItemTemplate}
        onChange={(selected) => {
          if (selected && !Array.isArray(selected)) numberingSystemOnChange(args.level)(selected);
        }}
        value={numberingSystemOptions.find((item) => item.value === args.numberingSystem)!}
        fields={{ text: "item", value: "value" }}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        options={numberingSystemOptions}
        // buttonWidth="100%"
        disabled={!actionPermissionAllowed || buttonLoadingRef.current || !!estimateVersionId}
      />,
    );
    return container;
  };

  const previewTemplate = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, args: { preview: number }) => {
    const container = document.createElement("div");
    const preview = generateNumberingSystemPreview(args.preview);
    const root = createRoot(container);
    root.render(<div>{preview}</div>);
    return container;
  };

  const preferencesGridColumns = useRef([
    {
      field: "level",
      name: t("Level Number"),
      textAlign: "Left",
    },
    {
      field: "numberingSystem",
      name: t("Numbering System"),
      textAlign: "Left",
      formatter: numberingSystemTemplate,
    },
    {
      field: "preview",
      name: t("Preview"),
      textAlign: "Left",
      formatter: previewTemplate,
    },
  ]);

  const numberingSystemContent = () => (
    <div className="overflow-auto h-115">
      <SlickGrid1
        ref={dataGrid as any}
        columnDefinitions={preferencesGridColumns.current as any}
        enableRowResize
        gridOptions={{
          enableAutoSizeColumns: true,
        }}
        rowHeight={41 as any}
        dataset={numberingSystemDataSource}
        showPageCount={false}
      />
    </div>
  );

  // Single declaration-site tag: the pre-existing "" fallback is not a 24-hex id, so it cannot be
  // branded by useParams alone. Runtime behaviour is unchanged.
  const estimateIdForUnits = (params.estimateId ?? "") as ObjectIdString;
  const unitsContent = () => <DisplayUnits estimateId={estimateIdForUnits} />;

  const raoundingPreferenceContent = () => (
    <div className="flex gap-8 flex-col pt-4">
      {preferencesContent("quantityRoundingPreference")}
      {preferencesContent("rateAmountRoundingPreference")}
    </div>
  );

  const getAccordianContent = (fieldText: string) => {
    switch (fieldText) {
      case estimateGeneralSettingFields.PREFERENCES.field:
        return <div className="flex w-full pt-4">{preferencesContent()}</div>;
      case estimateGeneralSettingFields.NUMBERING_SYSTEM.field:
        return numberingSystemContent();
      case estimateGeneralSettingFields.ROUNDING_PREFERENCES.field:
        return raoundingPreferenceContent();
      case estimateGeneralSettingFields.UNITS.field:
        return unitsContent();
    }
  };

  const fieldAccordian = (field: EstimateGeneralFieldKey) => {
    return (
      <div className="flex w-full border-0 border-b border-button_light">
        <Accordian
          className="w-full rounded-none border-0 shadow-none"
          expanded={accordianState[field]}
          onChange={() =>
            setAccordianState((prev) => ({
              ...prev,
              [field]: !prev[field],
            }))
          }
        >
          <AccordionSummary className="p-6" expandIcon={<ExpandMoreIcon />}>
            <div className="w-full text-xl font-semibold">{estimateGeneralSettingFields[field].text}</div>
          </AccordionSummary>
          <AccordionDetails className="px-6 pb-4 pt-0 border-0">
            {getAccordianContent(estimateGeneralSettingFields[field].field)}
          </AccordionDetails>
        </Accordian>
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-full overflow-auto">
      {(Object.keys(estimateGeneralSettingFields) as EstimateGeneralFieldKey[]).map((field: EstimateGeneralFieldKey) =>
        fieldAccordian(field),
      )}
    </div>
  );
};

export default EstimateSettings;
